<template>
  <div class="root">
    <LoadingSpinner v-if="loadState === 'loading'" size="medium" variant="inline" />

    <p v-else-if="loadState === 'error'" class="load-error" role="alert">
      {{ t("contestEntryForm.step3.contestLoadError") }}
    </p>

    <div v-else-if="contest" class="contents">
      <section class="panel">
        <img
          class="complete-icon"
          src="@/assets/img/contest-entry-complete-check.svg"
          alt=""
          width="80"
          height="80"
        />
        <h1 class="title">{{ t("contestEntryForm.completed.title") }}</h1>
        <div class="message">
          <i18n path="contestEntryForm.completed.lead" tag="p">
            <strong place="contestName" class="contest-name">{{ contest.contest }}</strong>
          </i18n>
          <p>{{ t("contestEntryForm.completed.changeNotice") }}</p>
          <p>{{ t("contestEntryForm.completed.manageNotice") }}</p>
        </div>
        <div class="actions">
          <router-link class="action-button action-button-primary" :to="{ name: 'entries' }">
            {{ t("contestEntryForm.completed.history") }}
          </router-link>
          <router-link class="action-button action-button-secondary" :to="{ name: 'home' }">
            {{ t("contestEntryForm.completed.home") }}
          </router-link>
        </div>
      </section>

      <ContestEntryFormContestAside :contest="contest" />
    </div>
  </div>
</template>

<script lang="ts">
export default {
  metaInfo: {
    meta: [{ name: "robots", content: "none" }],
  },
};
</script>

<script setup lang="ts">
import { defineProps, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n-bridge";
import LoadingSpinner from "@/components/atoms/LoadingSpinner.vue";
import ContestEntryFormContestAside from "@/components/organisms/event/ContestEntryFormContestAside.vue";
import { NolaPfContestClient } from "@/lib/clients";
import { NolaPfMicroCmsContest } from "@/lib/models/nolaPfContest";

const props = defineProps<{
  id?: string;
}>();

const { t } = useI18n();
const nolaPfContestClient = new NolaPfContestClient();

type LoadState = "loading" | "loaded" | "error";

const loadState = ref<LoadState>("loading");
const contest = ref<NolaPfMicroCmsContest | null>(null);

onMounted(async () => {
  const id = props.id;
  if (!id) {
    loadState.value = "error";
    return;
  }

  try {
    contest.value = await nolaPfContestClient.fetchMicroCmsContest(id);
    loadState.value = "loaded";
  } catch (error) {
    console.error("Failed to load contest entry completed page:", error);
    loadState.value = "error";
  }
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.root {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: calc(100vh - #{$header-height});
  overflow: hidden;
  padding: 40px 16px;
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);
}

.load-error {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-default);
}

.contents {
  display: flex;
  flex: 1;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  min-height: 0;
}

.panel {
  display: flex;
  flex: 1 1 740px;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  min-width: 0;
  padding: 40px 40px 48px;
  border-radius: var(--radius-md) var(--radius-md) 0 0;
  background-color: var(--color-surface-base);

  @include mq("pc") {
    align-self: stretch;
    min-height: 0;
    overflow-y: auto;
  }
}

.complete-icon {
  display: block;
}

.title {
  margin: 24px 0 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
  text-align: center;
  word-break: break-word;
}

.message {
  align-self: stretch;
  margin-top: 24px;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-default);
  word-break: break-word;

  p {
    margin: 0;
  }
}

.contest-name {
  font-weight: var(--font-weight-bold);
}

.actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 40px;
}

.action-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 188px;
  height: 40px;
  padding: 8px 24px;
  border-radius: var(--radius-rounded);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
  text-align: center;
  text-decoration: none;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}

.action-button-primary {
  background-color: var(--color-primary);
  color: var(--color-text-on-fixed-bg);
}

.action-button-secondary {
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
}

@include mq("sp") {
  .root {
    height: 100%;
    min-height: 0;
    padding: 0;
  }

  .load-error {
    padding: 24px 16px;
  }

  .contents {
    flex-direction: column;
    align-items: stretch;
    gap: 0;
  }

  .panel {
    flex: 1 1 auto;
    width: 100%;
    min-height: 0;
    padding: 40px 16px 32px;
    overflow-y: auto;
    border-radius: 0;
  }
}
</style>
